import Terms from "@/apollo/datasources/terms";
import Uid2 from "@/apollo/datasources/uid2";
import config from "@/lib/config";
import Cookies from "@/lib/cookies";
import dayjs from "@/lib/day";
import debugLog from "@/lib/debugLog";

/**
 * Uid2Tokenの取得、管理を行うクラス
 *
 * @param {object} req - リクエスト
 * @param {object} res - レスポンス
 * @constructor
 */
class Uid2Token {
  constructor(req, res) {
    this.req = req;
    this.res = res;

    this.uid2 = new Uid2({ req, res });
    // this.uid2.initialize({ context: { req, res } });
    this.terms = new Terms({ req, res });
    // this.terms.initialize({ context: { req, res } });
    this.cookies = new Cookies(req, res);
  }

  /**
   * getToken.
   *
   * uid2トークンの取得
   *
   * @return {Promise<string>} advertising_token
   */
  async getToken() {
    if (!this.req.userToken) return null;
    const data = this.getData();
    debugLog("[uid2] from cookie", data);
    if (data?.uid2?.advertising_token) {
      // AdvertisingTokenを更新すべきかを確認
      const now = dayjs();
      const refreshFrom = dayjs(data.uid2.refresh_from);
      const refreshTokenExpires = dayjs(data.uid2.refresh_expires);
      debugLog(
        `[uid2] refreshFrom: ${refreshFrom.format()}, refreshTokenExpires: ${refreshTokenExpires.format()}`
      );
      if (now.isBefore(refreshFrom)) {
        debugLog("[uid2] now is before refreshFrom");
        return data.uid2.advertising_token;
      } else {
        // リフレッシュトークンの有効期間を確認
        if (now.isBefore(refreshTokenExpires)) {
          debugLog("[uid2] now is before refreshTokenExpires");
          // refresh
          const refreshData = await this.refresh();
          return refreshData?.uid2?.advertising_token;
        } else {
          // generate
          const generateData = await this.generate();
          return generateData?.uid2?.advertising_token;
        }
      }
    } else {
      // generate
      const generateData = await this.generate();
      return generateData?.uid2?.advertising_token;
    }
  }

  /**
   * getData.
   *
   * Cookieに保存されているuid2トークンのデータを取得
   *
   * @return {Object}
   */
  getData() {
    const value = this.cookies.get(config.uid2tokenCookieName);
    if (value) {
      return JSON.parse(Buffer.from(value, "base64").toString());
    } else {
      return null;
    }
  }

  /**
   * setData.
   *
   * APIで取得したuid2トークンのデータをcookieに保存
   *
   * @param {object} data
   */
  setData(data) {
    this.cookies.set({
      name: config.uid2tokenCookieName,
      value: Buffer.from(JSON.stringify(data)).toString("base64"),
    });
  }

  /**
   * generate.
   *
   * UID2トークンの生成
   *
   * @return {Promise<Uid2Result|null>} data
   */
  async generate() {
    const data = await this.uid2.generate();
    debugLog("[uid2] generate token", data);
    if (data && data?.status === Uid2.STATUS_SUCCESS) {
      this.setData({ email_hash: data.email_hash, uid2: data.uid2 });
      return data;
    }
    return null;
  }

  /**
   * refresh.
   *
   * UID2トークンのリフレッシュ
   *
   * @return {Promise<Uid2Result|null>} data
   */
  async refresh() {
    const tokenValue = this.getData();
    if (tokenValue) {
      const data = await this.uid2.refresh(
        tokenValue.uid2.refresh_token,
        tokenValue.uid2.refresh_response_key,
        tokenValue.email_hash
      );
      debugLog("[uid2] refresh token", data);
      if (data && data?.status === Uid2.STATUS_SUCCESS) {
        this.setData({ email_hash: data.email_hash, uid2: data.uid2 });
        return data;
      }
    }
    return null;
  }

  /**
   * delete.
   *
   * uid2トークン情報の削除
   */
  delete() {
    this.cookies.remove([
      config.uid2tokenCookieName,
      config.adTermsConfirmCookie.name,
    ]);
  }

  /**
   * isAdTermsAgree.
   *
   * 広告規約に同意しているかどうか
   *
   * @return {Promise<boolean>} result
   */
  async isAdTermsAgree() {
    const { name: cookieName } = config.adTermsConfirmCookie;
    const confirmed = this.cookies.get(cookieName);
    if (!confirmed) {
      const termsData = await this.terms.getTerm(
        config.terms.advertisingTerm,
        true
      );
      // 一定期間キャッシュする
      return this.adTermsAgreeCache(termsData);
    } else {
      const parsedJson = JSON.parse(confirmed);
      return parsedJson?.result;
    }
  }

  adTermsAgreeCache(termsData) {
    const { name: cookieName, maxAge } = config.adTermsConfirmCookie;
    const { current_revision: rev } = termsData;
    debugLog(`[uid2] ad terms agree? ${JSON.stringify(rev)}`);
    const result = rev?.number === rev?.agreement?.term_revision_number;
    this.cookies.set({
      name: cookieName,
      value: JSON.stringify({ result }),
      options: { maxAge },
    });
    return result;
  }

  clearAdTermsAgreeCache() {
    this.cookies.remove(config.adTermsConfirmCookie.name);
  }
}

export default Uid2Token;
